import {
  NormasTeachingItemType,
  PaymentType,
  StripeOrderDTO,
  StripeOrderStatus,
} from "../../Stripe/StripeOrderDTO.ts";
import { canSettleInstallments } from "./InstallmentsSettlement/canSettleInstallments.ts";
import { BankTransferSettlementDetails } from "./InstallmentsSettlement/BankTransferSettlementDetails.tsx";
import {
  describeStripePaymentMethod,
  describeStripePaymentType,
} from "../../Stripe/stripePaymentLabels.ts";
import { usePopupController } from "../../HeyLight/usePopupController.ts";
import { useCancelSubscriptionStripeOrder } from "./useCancelSubscriptionStripeOrder.ts";
import { useMessageController } from "../../UI/useMessageController.ts";
import { BusinessType } from "../../MoneyTransfer/BusinessType.tsx";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { Dialog } from "@headlessui/react";
import { SuccessMessage } from "../../UI/Success/SuccessMessage.tsx";
import copy from "copy-to-clipboard";
import dayjs from "dayjs";
import { InstallmentInvoicesSection } from "./InstallmentInvoicesSection.tsx";
import { OutlinedButton, DangerButton } from "@normasteaching/norma-ui";
import { X, CircleX, Pencil, Trash2, Landmark } from "lucide-react";
import { ErrorPopup } from "../../UI/Error/ErrorPopup.tsx";
import { useUpdateInvoiceBillingInfo } from "./useUpdateInvoiceBillingInfo.ts";
import { OrderStatusRenderer } from "./StripeOrderStatusRenderer.tsx";
import { ExpandableCard } from "../../UI/ExpandableCard.tsx";
import { useState } from "react";
import { EditInvoiceInfoModal } from "./EditInvoiceInfoModal.tsx";
import { useGetStripeRefundRecord } from "./useGetStripeRefundRecord.ts";
import { OneOnOneLessonType } from "../../OneOnOneLessonType.ts";
import { formatDateRome } from "../../UI/Utils/formatDateRome.ts";
import { usePurchasedItemNames } from "../../ProductService/PurchasedItem/usePurchasedItemNames.ts";
import { CopyableText } from "../../UI/CopyableText.tsx";

export function OrderDetailsModal({
  order,
  onClose,
  onOrderUpdated,
  onSettleInstallments,
  readOnly,
}: {
  order: StripeOrderDTO;
  onClose: () => void;
  onOrderUpdated?: (updatedOrder: StripeOrderDTO) => void;
  onSettleInstallments?: (order: StripeOrderDTO) => void;
  readOnly?: boolean;
}) {
  const [currentOrder, setCurrentOrder] = useState(order);
  const { resolveName } = usePurchasedItemNames();
  const {
    openPopup,
    closePopup,
    open: popupOpen,
    title,
    description,
  } = usePopupController();
  const isRefunded =
    currentOrder.status === StripeOrderStatus.Refunded ||
    currentOrder.status === StripeOrderStatus.PartiallyRefunded;
  const { refund: refundRecord, loading: loadingRefund } =
    useGetStripeRefundRecord(isRefunded ? currentOrder._id : null);
  const { cancelSubscriptionStripeOrder, loading: loadingCancelSubscription } =
    useCancelSubscriptionStripeOrder();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditInvoiceModal, setShowEditInvoiceModal] = useState(false);
  const [showRemoveInvoiceModal, setShowRemoveInvoiceModal] = useState(false);
  const { updateInvoiceBillingInfo, loading: loadingRemoveInvoice } =
    useUpdateInvoiceBillingInfo();

  const handleRemoveInvoiceInfo = async () => {
    try {
      // The backend replaces the whole invoiceInfo subdocument, so sending the
      // bare { requested: false } is what drops the billing fields. The
      // customer name is required by the endpoint and must not change here.
      const updatedOrder = await updateInvoiceBillingInfo(
        currentOrder._id,
        { requested: false },
        {
          firstName: currentOrder.customerInformation.firstName,
          lastName: currentOrder.customerInformation.lastName,
        },
      );
      setCurrentOrder(updatedOrder);
      setShowRemoveInvoiceModal(false);
      onOrderUpdated?.(updatedOrder);
    } catch (e) {
      const err = e as Error;
      setShowRemoveInvoiceModal(false);
      openPopup({
        title: "Errore",
        description: `Si è verificato un errore durante la rimozione dei dati di fatturazione: ${err?.message ?? "Errore sconosciuto"}`,
        timeoutMs: 5000,
      });
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await cancelSubscriptionStripeOrder(currentOrder);
      setShowConfirmModal(false);
      onClose();
    } catch (e) {
      const err = e as Error;
      setShowConfirmModal(false);
      openPopup({
        title: "Errore",
        description: `Si è verificato un errore durante la cancellazione della sottoscrizione: ${err?.message ?? "Errore sconosciuto"}`,
        timeoutMs: 5000,
      });
    }
  };

  const { showMessage, message, open } = useMessageController();

  const renderInvoiceInfo = () => {
    if (!currentOrder.invoiceInfo.requested) {
      return <p className="text-gray-600">Fattura non richiesta</p>;
    }

    const info = currentOrder.invoiceInfo;
    return (
      <div className="space-y-2">
        <div>
          <span className="font-medium">Tipo:</span>{" "}
          {info.businessType === BusinessType.Person
            ? "Persona Fisica"
            : info.businessType === BusinessType.Company
              ? "Azienda"
              : "Ditta Individuale"}
        </div>
        {info.businessType === BusinessType.Company &&
          "businessName" in info && (
            <div>
              <span className="font-medium">Ragione Sociale:</span>{" "}
              {info.businessName}
            </div>
          )}
        <div>
          <span className="font-medium">Codice Fiscale:</span> {info.taxCode}
        </div>
        {info.businessType !== BusinessType.Person && "vat" in info && (
          <div>
            <span className="font-medium">P.IVA:</span> {info.vat}
          </div>
        )}
        {info.businessType !== BusinessType.Person &&
          "pec" in info &&
          info.pec && (
            <div>
              <span className="font-medium">PEC:</span> {info.pec}
            </div>
          )}
        {info.businessType !== BusinessType.Person &&
          "sdiCode" in info &&
          info.sdiCode && (
            <div>
              <span className="font-medium">Codice SDI:</span> {info.sdiCode}
            </div>
          )}
        <div>
          <span className="font-medium">Indirizzo:</span> {info.address.street},{" "}
          {info.address.cap} {info.address.city} ({info.address.province})
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Without this the openPopup calls below never surface anything. */}
      <ErrorPopup
        description={description}
        title={title}
        onClose={closePopup}
        open={popupOpen}
      />

      <Modal
        isOpen={true}
        onClose={onClose}
        title="Dettagli Ordine"
        overridePanel={({ title, children }) => (
          <Dialog.Panel className="flex-1 md:flex-auto md:max-w-4xl transform overflow-hidden md:rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
            <Dialog.Title
              as="h3"
              className="text-lg font-medium leading-6 text-gray-900"
            >
              {title}
            </Dialog.Title>
            <div className="mt-4">{children}</div>
          </Dialog.Panel>
        )}
      >
        <SuccessMessage open={open} message={message} />
        <div className="space-y-6 max-h-[70vh] overflow-y-auto max-w-4xl">
          {/* Informazioni generali */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
              Informazioni Generali
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="font-medium text-gray-700">Order ID:</span>
                <p
                  className="text-gray-600 cursor-pointer hover:text-blue-600"
                  onClick={() => {
                    copy(currentOrder._id);
                    showMessage({
                      message: "ID dell'ordine copiato negli appunti!",
                      timeoutMs: 4000,
                    });
                  }}
                >
                  {currentOrder._id}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700">
                  Data creazione:
                </span>
                <p className="text-gray-600">
                  {dayjs(currentOrder.createdAt).format("DD/MM/YYYY HH:mm:ss")}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Stato:</span>
                <div className="mt-1">
                  <OrderStatusRenderer status={currentOrder.status} />
                </div>
              </div>
              {currentOrder.status === StripeOrderStatus.Failed && (
                <div>
                  <span className="font-medium text-gray-700">
                    Motivo del rifiuto:
                  </span>
                  <p className="text-gray-600">
                    {currentOrder.failureCode ?? "N/A"}
                    {currentOrder.failureMessage
                      ? ` — ${currentOrder.failureMessage}`
                      : ""}
                  </p>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-700">Prezzo:</span>
                <div className="space-y-1">
                  {currentOrder.coupon ? (
                    <>
                      <p className="text-gray-400 line-through text-sm">
                        €{(currentOrder.amountInCent.amount / 100).toFixed(2)}{" "}
                        {currentOrder.amountInCent.currency.toUpperCase()}
                      </p>
                      <p className="text-gray-600 ">
                        €
                        {(
                          (currentOrder.amountInCent.amount -
                            currentOrder.coupon.value) /
                          100
                        ).toFixed(2)}{" "}
                        {currentOrder.amountInCent.currency.toUpperCase()}
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-600">
                      €{(currentOrder.amountInCent.amount / 100).toFixed(2)}{" "}
                      {currentOrder.amountInCent.currency.toUpperCase()}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <span className="font-medium text-gray-700">
                  Tipologia di pagamento:
                </span>
                <p className="text-gray-600">
                  {describeStripePaymentType(currentOrder)}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700">
                  {currentOrder.productType === NormasTeachingItemType.Bundle
                    ? "Bundle:"
                    : "Prodotto:"}
                </span>
                <p className="text-gray-600 truncate">
                  <CopyableText
                    className="cursor-pointer hover:text-blue-600"
                    value={currentOrder.normasTeachingProductId}
                    title={currentOrder.normasTeachingProductId}
                    copyMessage={`ID del ${currentOrder.productType == NormasTeachingItemType.Bundle ? "bundle" : "prodotto"} copiato negli appunti!`}
                    showMessage={showMessage}
                  >
                    {
                      resolveName({
                        id: currentOrder.normasTeachingProductId,
                        type: currentOrder.productType,
                        snapshot: currentOrder.productSnapshot,
                      }).name
                    }
                  </CopyableText>
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700">
                  Metodo di pagamento:
                </span>
                <p
                  className="text-gray-600 truncate"
                  title={currentOrder.paymentMethod}
                >
                  {describeStripePaymentMethod(currentOrder.paymentMethod)}
                  {currentOrder.cardLast4Digits &&
                  currentOrder.cardLast4Digits != ""
                    ? ` - *${currentOrder.cardLast4Digits}`
                    : ""}
                </p>
              </div>
            </div>
          </div>

          {/* Informazioni Cliente */}
          <ExpandableCard title="Informazioni Cliente">
            <div>
              <span className="font-medium text-gray-700">Nome:</span>
              <p className="text-gray-600">
                {currentOrder.customerInformation.firstName}{" "}
                {currentOrder.customerInformation.lastName}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Email:</span>
              <p
                className="text-gray-600 cursor-pointer hover:text-blue-600"
                onClick={() => {
                  copy(currentOrder.customerInformation.email);
                  showMessage({
                    message: "Email copiata negli appunti!",
                    timeoutMs: 4000,
                  });
                }}
              >
                {currentOrder.customerInformation.email}
              </p>
            </div>
          </ExpandableCard>

          {/* Informazioni Fattura */}
          <ExpandableCard
            title="Informazioni Fattura"
            headerAction={
              readOnly ? undefined : (
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEditInvoiceModal(true);
                    }}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <Pencil className="w-4 h-4" />
                    Modifica
                  </button>
                  {/* Nothing to remove when the invoice was never requested. */}
                  {currentOrder.invoiceInfo.requested && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowRemoveInvoiceModal(true);
                      }}
                      className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                      Rimuovi
                    </button>
                  )}
                </div>
              )
            }
          >
            {renderInvoiceInfo()}
          </ExpandableCard>

          {/* Informazioni Coupon/Sconto */}
          {currentOrder.coupon && (
            <ExpandableCard title="Coupon Applicato">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">Codice:</span>
                <code className="bg-white px-2 py-1 rounded border border-gray-300 text-gray-700 font-mono">
                  {currentOrder.coupon.couponCode}
                </code>
              </div>
              <div>
                <span className="font-medium text-gray-900">ID Coupon:</span>
                <span className="text-gray-700 ml-2 text-sm font-mono">
                  {currentOrder.coupon._id}
                </span>
              </div>
            </ExpandableCard>
          )}

          {/* Informazioni One on One */}
          {currentOrder.productSnapshot?.fpsProductInfo?.oneOnOne && (
            <ExpandableCard title="Informazioni One on One">
              <div>
                <span className="font-medium text-gray-700">
                  Totale lezioni:
                </span>
                <p className="text-gray-600">
                  {currentOrder.productSnapshot.fpsProductInfo
                    .oneOnOneTotalLessons ?? 0}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700">
                  Tipo di lezione:
                </span>
                <p className="text-gray-600">
                  {currentOrder.productSnapshot.fpsProductInfo
                    .oneOnOneLessonType === OneOnOneLessonType.GeneralEnglish
                    ? "General English"
                    : currentOrder.productSnapshot.fpsProductInfo
                          .oneOnOneLessonType ===
                        OneOnOneLessonType.BusinessEnglish
                      ? "Business English"
                      : "N/A"}
                </p>
              </div>
            </ExpandableCard>
          )}

          {/* Storico Rate */}
          {currentOrder.numberOfInstallments &&
            currentOrder.installmentInvoices && (
              <ExpandableCard title="Storico Rate">
                <InstallmentInvoicesSection
                  installmentInvoices={currentOrder.installmentInvoices}
                />
              </ExpandableCard>
            )}

          {currentOrder.bankTransferSettlement && (
            <ExpandableCard title="Saldo rate con bonifico" defaultExpanded>
              <BankTransferSettlementDetails
                settlement={currentOrder.bankTransferSettlement}
                orderId={currentOrder._id}
              />
            </ExpandableCard>
          )}

          {/* Partecipanti Bundle */}
          {currentOrder.bundleParticipants &&
            currentOrder.bundleParticipants.length > 0 && (
              <ExpandableCard title="Partecipanti Bundle">
                {currentOrder.bundleParticipants.map((participant, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 p-3 rounded-md space-y-2"
                  >
                    <div className="font-medium text-gray-900">
                      Partecipante {index + 1}
                    </div>
                    <div>
                      <span className="text-sm text-gray-700">Nome:</span>{" "}
                      {participant.customer.firstName}{" "}
                      {participant.customer.lastName}
                    </div>
                    <div>
                      <span className="text-sm text-gray-700">Email:</span>{" "}
                      <span
                        className="text-gray-600 cursor-pointer hover:text-blue-600"
                        onClick={() => {
                          copy(participant.customer.email);
                          showMessage({
                            message: "Email copiata negli appunti!",
                            timeoutMs: 4000,
                          });
                        }}
                      >
                        {participant.customer.email}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <span className="text-sm text-gray-700">Prodotti:</span>
                      {participant.products.map((product, pIndex) => (
                        <div
                          key={pIndex}
                          className="text-sm text-gray-600 ml-3"
                        >
                          •{" "}
                          <CopyableText
                            className="cursor-pointer hover:text-blue-600"
                            value={product.id}
                            title={product.id}
                            copyMessage="ID del prodotto copiato negli appunti!"
                            showMessage={showMessage}
                          >
                            {
                              resolveName({
                                id: product.id,
                                type: NormasTeachingItemType.Product,
                              }).name
                            }
                          </CopyableText>{" "}
                          - €{(product.priceInCents.amount / 100).toFixed(2)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </ExpandableCard>
            )}

          {/* Altri Partecipanti */}
          {currentOrder.participants &&
            currentOrder.participants.length > 0 && (
              <ExpandableCard title="Altri Partecipanti">
                {currentOrder.participants.map((participant, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 p-3 rounded-md space-y-1"
                  >
                    <div>
                      <span className="text-sm text-gray-700">Nome:</span>{" "}
                      {participant.firstName} {participant.lastName}
                    </div>
                    <div>
                      <span className="text-sm text-gray-700">Email:</span>{" "}
                      <span
                        className="text-gray-600 cursor-pointer hover:text-blue-600"
                        onClick={() => {
                          copy(participant.email);
                          showMessage({
                            message: "Email copiata negli appunti!",
                            timeoutMs: 4000,
                          });
                        }}
                      >
                        {participant.email}
                      </span>
                    </div>
                  </div>
                ))}
              </ExpandableCard>
            )}

          {/* Rimborso */}
          {isRefunded && (
            <ExpandableCard title="Rimborso">
              {loadingRefund ? (
                <p className="text-gray-500 text-sm">Caricamento...</p>
              ) : refundRecord ? (
                <>
                  <div>
                    <span className="font-medium text-gray-700">
                      Importo rimborsato:
                    </span>
                    <p className="text-gray-600 font-medium">
                      €{(refundRecord.amountInCent.amount / 100).toFixed(2)}{" "}
                      {refundRecord.amountInCent.currency.toUpperCase()}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Tipo:</span>
                    <p className="text-gray-600">
                      {refundRecord.totalRefund
                        ? "Rimborso totale"
                        : "Rimborso parziale"}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Data:</span>
                    <p className="text-gray-600">
                      {formatDateRome(refundRecord.createdAt)}
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-gray-500 text-sm">
                  Nessun record di rimborso trovato.
                </p>
              )}
            </ExpandableCard>
          )}

          {/* Pulsanti */}
          <div className="flex justify-between items-start gap-2">
            <OutlinedButton
              iconLeft={<X className={"w-5"} />}
              onClick={onClose}
              label="Chiudi"
            />
            {!readOnly && (
              <div className="flex flex-wrap justify-end gap-2">
                {onSettleInstallments &&
                  currentOrder.paymentType === PaymentType.Installments && (
                    <OutlinedButton
                      iconLeft={<Landmark className={"w-5"} />}
                      onClick={() => onSettleInstallments(currentOrder)}
                      disabled={!canSettleInstallments(currentOrder)}
                      label="Saldo rate con bonifico"
                    />
                  )}
                <DangerButton
                  iconLeft={<CircleX className={"w-5"} />}
                  onClick={() => setShowConfirmModal(true)}
                  disabled={
                    currentOrder.status !== StripeOrderStatus.InstallmentPaid
                  }
                  label="Cancella Subscription"
                />
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Modal modifica dati fatturazione */}
      {!readOnly && showEditInvoiceModal && (
        <EditInvoiceInfoModal
          orderId={currentOrder._id}
          invoiceInfo={currentOrder.invoiceInfo}
          customerInfo={{
            firstName: currentOrder.customerInformation.firstName,
            lastName: currentOrder.customerInformation.lastName,
          }}
          onClose={() => setShowEditInvoiceModal(false)}
          onSaved={(updatedOrder) => {
            setCurrentOrder(updatedOrder);
            setShowEditInvoiceModal(false);
            onOrderUpdated?.(updatedOrder);
          }}
        />
      )}

      {/* Modal di conferma rimozione dati di fatturazione */}
      <Modal
        isOpen={!readOnly && showRemoveInvoiceModal}
        onClose={() => {
          if (!loadingRemoveInvoice) setShowRemoveInvoiceModal(false);
        }}
        title="Rimuovi dati di fatturazione"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Sei sicuro di voler rimuovere i dati di fatturazione da questo
            ordine?
          </p>
          <p className="text-sm text-gray-600">
            L'ordine tornerà a risultare{" "}
            <span className="font-semibold">senza fattura richiesta</span>.
            Codice fiscale, P.IVA, PEC, codice SDI e indirizzo verranno
            eliminati dall'ordine{" "}
            <span className="font-mono font-semibold">{currentOrder._id}</span>{" "}
            e non sono recuperabili.
          </p>
          <div className="flex justify-end gap-3 mt-6">
            <OutlinedButton
              iconLeft={<X className={"w-5"} />}
              onClick={() => setShowRemoveInvoiceModal(false)}
              label="Annulla"
              disabled={loadingRemoveInvoice}
            />
            <DangerButton
              iconLeft={<Trash2 className={"w-5"} />}
              onClick={handleRemoveInvoiceInfo}
              label="Conferma rimozione"
              loading={loadingRemoveInvoice}
            />
          </div>
        </div>
      </Modal>

      {/* Modal di conferma cancellazione */}
      <Modal
        isOpen={!readOnly && showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Conferma Cancellazione"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Sei sicuro di voler cancellare questa subscription?
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-semibold">Silent Cancellation</span>: Questa
            azione annullerà tutti i pagamenti futuri programmati per l'ordine{" "}
            <span className="font-mono font-semibold">{currentOrder._id}</span>.
          </p>
          <div className="flex justify-end gap-3 mt-6">
            <OutlinedButton
              iconLeft={<X className={"w-5"} />}
              onClick={() => setShowConfirmModal(false)}
              label="Annulla"
              disabled={loadingCancelSubscription}
            />
            <DangerButton
              iconLeft={<CircleX className={"w-5"} />}
              onClick={handleCancelSubscription}
              label="Conferma Cancellazione"
              loading={loadingCancelSubscription}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
